<template>
  <section class="rk-features-page" id="features-overview">
    <div class="rk-container">
      <div class="rk-section__head">
        <div class="rk-kicker">
          <span class="rk-kicker__rule"></span>
          <span class="rk-kicker__text"><i class="bi bi-grid"></i> What's inside</span>
        </div>
        <h2>Three parts of the job, handled without extra tools</h2>
      </div>

      <!-- Row 1: Tenant management -->
      <div class="feature-row">
        <div class="feature-copy">
          <p class="feature-kicker">Units &amp; tenants</p>
          <h3>Every unit, every tenant, one record</h3>
          <p class="feature-desc">
            Register tenants, assign them to units, and keep lease details,
            deposits, and contacts together — instead of scattered across
            notebooks and phone contacts.
          </p>
          <ul class="feature-points">
            <li><CheckIcon /> Assign tenants to units in a few taps</li>
            <li><CheckIcon /> Track lease start dates and deposits</li>
            <li><CheckIcon /> Auto-generate invoices every month</li>
          </ul>
        </div>

        <div class="feature-visual" aria-hidden="true">
          <div class="unit-card">
            <div class="unit-card-head">
              <span>Unit B-14</span>
              <span class="unit-tag">Occupied</span>
            </div>
            <div class="unit-card-row">
              <span class="uc-label">Tenant</span>
              <span class="uc-value">A. Mwangi</span>
            </div>
            <div class="unit-card-row">
              <span class="uc-label">Rent</span>
              <span class="uc-value">KES 32,000 / mo</span>
            </div>
            <div class="unit-card-row">
              <span class="uc-label">Lease start</span>
              <span class="uc-value">4 Feb 2025</span>
            </div>
            <div class="unit-card-row">
              <span class="uc-label">Deposit held</span>
              <span class="uc-value">KES 32,000</span>
            </div>
          </div>
        </div>
      </div>

      <div class="row-divider"></div>

      <!-- Row 2: Automated communications (reversed) -->
      <div class="feature-row reversed">
        <div class="feature-visual" aria-hidden="true">
          <div class="message-card">
            <div class="message-card-head">
              <span class="message-avatar">JO</span>
              <div>
                <p class="message-name">SMS · J. Otieno</p>
                <p class="message-time">Sent automatically · 9:00 AM</p>
              </div>
            </div>
            <p class="message-body">
              "Rent for Unit A-02 (KES 28,000) is due on 9 Jul. Pay via
              M-Pesa Paybill 400200, account A02OTIENO."
            </p>
            <div class="message-status">Delivered</div>
          </div>
        </div>

        <div class="feature-copy">
          <p class="feature-kicker">Communication</p>
          <h3>Reminders and receipts, sent without asking</h3>
          <p class="feature-desc">
            Automated SMS go out for viewings, rent reminders, and receipts
            the moment a payment lands — so tenants stay informed and your
            phone stays quiet.
          </p>
          <ul class="feature-points">
            <li><CheckIcon /> Rent reminders before the due date</li>
            <li><CheckIcon /> Instant receipts after every payment</li>
            <li><CheckIcon /> Viewing confirmations for prospects</li>
          </ul>
        </div>
      </div>

      <div class="row-divider"></div>

      <!-- Row 3: three minimal columns -->
      <div class="feature-columns">
        <div class="feature-col">
          <ChartIcon />
          <h4>Real-time analytics</h4>
          <p>Occupancy, arrears, and income by property, updated as payments come in.</p>
        </div>
        <div class="feature-col">
          <ShieldIcon />
          <h4>Secure payments</h4>
          <p>M-Pesa transactions reconcile automatically against the right tenant and unit.</p>
        </div>
        <div class="feature-col">
          <DocIcon />
          <h4>Document storage</h4>
          <p>Leases, IDs, and inspection notes stored with the unit they belong to.</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { h } from 'vue'

const CheckIcon = () =>
  h(
    'svg',
    { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', 'aria-hidden': 'true' },
    [h('path', { d: 'M5 13l4 4L19 7', stroke: 'currentColor', 'stroke-width': 1.9, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' })]
  )

const ChartIcon = () =>
  h(
    'svg',
    { width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', 'aria-hidden': 'true', class: 'col-icon' },
    [h('path', { d: 'M4 20V10M12 20V4M20 20v-7', stroke: 'currentColor', 'stroke-width': 1.6, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' })]
  )

const ShieldIcon = () =>
  h(
    'svg',
    { width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', 'aria-hidden': 'true', class: 'col-icon' },
    [h('path', { d: 'M12 3l7 3v6c0 4.5-3 8-7 9-4-1-7-4.5-7-9V6l7-3z', stroke: 'currentColor', 'stroke-width': 1.6, 'stroke-linejoin': 'round' }),
     h('path', { d: 'M9.5 12l1.8 1.8L15 10', stroke: 'currentColor', 'stroke-width': 1.6, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' })]
  )

const DocIcon = () =>
  h(
    'svg',
    { width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', 'aria-hidden': 'true', class: 'col-icon' },
    [h('path', { d: 'M7 3.5h7l4 4V20a.8.8 0 0 1-.8.8H7A.8.8 0 0 1 6.2 20V4.3A.8.8 0 0 1 7 3.5z', stroke: 'currentColor', 'stroke-width': 1.6, 'stroke-linejoin': 'round' }),
     h('path', { d: 'M14 3.5V8h4', stroke: 'currentColor', 'stroke-width': 1.6, 'stroke-linejoin': 'round' }),
     h('path', { d: 'M9 12.5h6M9 15.8h6', stroke: 'currentColor', 'stroke-width': 1.6, 'stroke-linecap': 'round' })]
  )
</script>

<style scoped>
.rk-container { max-width: 1200px; margin: 0 auto; padding: 0 1.5rem; }

.rk-features-page {
  padding: 120px 0;
  background: var(--paper);
}

.rk-section__head {
  max-width: 640px;
  margin-bottom: 80px;
}

.rk-kicker {
  display: inline-flex; align-items: center; gap: 0.85rem;
  margin-bottom: 1rem;
}
.rk-kicker__rule { width: 32px; height: 3px; background: var(--brass); border-radius: 2px; flex-shrink: 0; }
.rk-kicker__text {
  display: inline-flex; align-items: center; gap: 0.5rem;
  font-family: var(--font-mono); font-size: 0.95rem; font-weight: 700;
  letter-spacing: 0.12em; text-transform: uppercase; color: var(--brass);
}
.rk-kicker__text i { font-size: 1rem; }

.rk-section__head h2 {
  font-family: var(--font-display);
  font-weight: 400;
  font-size: 2.6rem;
  letter-spacing: -0.01em;
  color: var(--ink);
  line-height: 1.15;
}

/* Alternating rows */
.feature-row {
  display: grid;
  grid-template-columns: 7fr 5fr;
  gap: 72px;
  align-items: center;
  padding: 24px 0;
}

.feature-row.reversed {
  grid-template-columns: 5fr 7fr;
}

.feature-row.reversed .feature-visual {
  order: 1;
}

.feature-row.reversed .feature-copy {
  order: 2;
}

.feature-kicker {
  font-family: var(--font-mono);
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--brass);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 12px;
}

.feature-copy h3 {
  font-family: var(--font-display);
  font-style: italic;
  font-weight: 400;
  font-size: 1.9rem;
  color: var(--ink);
  line-height: 1.3;
  margin-bottom: 16px;
}

.feature-desc {
  font-family: var(--font-body);
  font-size: 16px;
  line-height: 1.7;
  color: var(--slate);
  margin-bottom: 24px;
  max-width: 460px;
}

.feature-points {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.feature-points li {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-body);
  font-size: 14.5px;
  color: var(--ink);
  font-weight: 500;
}

.feature-points li :deep(svg) {
  color: var(--brass);
  flex-shrink: 0;
}

.row-divider {
  height: 1px;
  background: var(--line);
  margin: 56px 0;
}

/* Unit card visual */
.unit-card {
  background: var(--neu-surface);
  border-radius: var(--radius-lg);
  padding: 22px;
  max-width: 340px;
  margin-left: auto;
  box-shadow: var(--neu-raised);
}

.unit-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: var(--font-body);
  font-weight: 700;
  color: var(--ink);
  font-size: 15px;
  padding-bottom: 14px;
  margin-bottom: 14px;
  border-bottom: 1px solid var(--line);
}

.unit-tag {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 600;
  color: var(--brass);
  background: rgba(176,141,63,0.14);
  padding: 3px 10px;
  border-radius: 100px;
}

.unit-card-row {
  display: flex;
  justify-content: space-between;
  padding: 7px 0;
  font-family: var(--font-body);
  font-size: 13.5px;
}

.uc-label {
  color: var(--slate);
}

.uc-value {
  font-family: var(--font-mono);
  font-weight: 600;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}

/* Message card visual */
.message-card {
  background: var(--neu-surface);
  border-radius: var(--radius-lg);
  padding: 22px;
  max-width: 360px;
  box-shadow: var(--neu-raised);
}

.message-card-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.message-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(176,141,63,0.16);
  color: var(--brass);
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.message-name {
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
}

.message-time {
  font-family: var(--font-body);
  font-size: 12px;
  color: var(--slate-soft);
}

.message-body {
  font-family: var(--font-body);
  font-size: 14px;
  line-height: 1.6;
  color: var(--ink);
  background: var(--paper);
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 12px;
}

.message-status {
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 600;
  color: var(--brass);
}

/* Row 3: minimal columns */
.feature-columns {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
}

.feature-col {
  padding: 8px 32px;
  border-left: 1px solid var(--line);
}

.feature-col:first-child {
  border-left: none;
  padding-left: 0;
}

.feature-col :deep(.col-icon) {
  color: var(--brass);
  margin-bottom: 18px;
}

.feature-col h4 {
  font-family: var(--font-body);
  font-size: 17px;
  font-weight: 700;
  color: var(--ink);
  margin-bottom: 10px;
}

.feature-col p {
  font-family: var(--font-body);
  font-size: 14.5px;
  line-height: 1.65;
  color: var(--slate);
}

@media (max-width: 900px) {
  .feature-row,
  .feature-row.reversed {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .feature-row.reversed .feature-visual,
  .feature-row.reversed .feature-copy {
    order: initial;
  }

  .unit-card {
    margin-left: 0;
  }

  .feature-columns {
    grid-template-columns: 1fr;
    gap: 36px;
  }

  .feature-col {
    border-left: none;
    padding-left: 0;
    padding-right: 0;
    border-top: 1px solid var(--line);
    padding-top: 32px;
  }

  .feature-col:first-child {
    border-top: none;
    padding-top: 8px;
  }
}

@media (max-width: 560px) {
  .rk-features-page {
    padding: 80px 0;
  }

  .rk-section__head {
    margin-bottom: 56px;
  }

  .rk-section__head h2 {
    font-size: 1.9rem;
  }
}
</style>